import typia, { type tags } from 'typia';
import type { DirectedTriple, StartSetExpansion, ViewIds, } from '@ardoq/api-types';
import { parseOrNull } from '../charts/chartPayloads';
export const VIEW_PREVIEW_FENCE = 'ardoq-view-preview';
/** Which components and relationships to show, in the `TraversalParams` vocabulary. */
export type ViewPreviewTraversal = {
    startSet: string[];
    paths: DirectedTriple[][];
    startSetExpansion?: StartSetExpansion;
};
/** How to draw the traversal's components and relationships. */
export type ViewPreviewStyle = {
    viewName: ViewIds.DEPENDENCY_MAP_2 | ViewIds.MODERNIZED_BLOCK_DIAGRAM | ViewIds.TIMELINE | ViewIds.RELATIONSHIPS_3 | ViewIds.BUBBLE;
    milestoneFields?: string[] | 'all';
    bubbleFields?: {
        x?: string;
        y?: string;
        size?: string;
    };
};
export type ViewPreviewPayload = {
    traversal: ViewPreviewTraversal;
    view: ViewPreviewStyle;
};
export const validateViewPreviewPayload: (value: string) => ViewPreviewPayload | null = parseOrNull((() => { const _io0 = (input: any): boolean => "object" === typeof input.traversal && null !== input.traversal && _io1(input.traversal) && ("object" === typeof input.view && null !== input.view && _io3(input.view)); const _io1 = (input: any): boolean => Array.isArray(input.startSet) && input.startSet.every((elem: any) => "string" === typeof elem) && (Array.isArray(input.paths) && input.paths.every((elem: any) => Array.isArray(elem) && elem.every((elem: any) => "object" === typeof elem && null !== elem && _io2(elem)))) && (undefined === input.startSetExpansion || "descendants" === input.startSetExpansion); const _io2 = (input: any): boolean => "string" === typeof input.sourceType && "string" === typeof input.targetType && "string" === typeof input.referenceType && ("outgoing" === input.direction || "incoming" === input.direction); const _io3 = (input: any): boolean => ("bubble" === input.viewName || "timeline" === input.viewName || "dependencyMap" === input.viewName || "relationships3" === input.viewName || "modernizedBlockDiagram" === input.viewName) && (null !== input.milestoneFields && (undefined === input.milestoneFields || "all" === input.milestoneFields || Array.isArray(input.milestoneFields) && input.milestoneFields.every((elem: any) => "string" === typeof elem))) && (undefined === input.bubbleFields || "object" === typeof input.bubbleFields && null !== input.bubbleFields && false === Array.isArray(input.bubbleFields) && _io4(input.bubbleFields)); const _io4 = (input: any): boolean => (undefined === input.x || "string" === typeof input.x) && (undefined === input.y || "string" === typeof input.y) && (undefined === input.size || "string" === typeof input.size); const __is = (input: any): input is ViewPreviewPayload => "object" === typeof input && null !== input && _io0(input); return (input: string): import("typia").Primitive<ViewPreviewPayload> | null => { input = JSON.parse(input); return __is(input) ? input as any : null; }; })());
/** One step of a path: a reference type, its two component types, and which way to walk it. */
type ViewPreviewPathStepInput = {
    /** Component type name at the reference's source end, exactly as the metamodel spells it (case-sensitive). This is the reference's own source, whichever way the step is walked. */
    sourceType: string;
    /** Reference type name, exactly as the metamodel spells it (case-sensitive), e.g. `Depends on`. */
    referenceType: string;
    /** Component type name at the reference's target end, exactly as the metamodel spells it (case-sensitive). This is the reference's own target, whichever way the step is walked. */
    targetType: string;
    /** Which way to walk the reference. `outgoing` walks from source to target (what X points to, e.g. what X depends on); `incoming` walks from target to source (what points to X, e.g. what depends on X). The first step starts from the start components, so their type must be `sourceType` for `outgoing` and `targetType` for `incoming`; each later step starts from the type the previous step ended on. */
    direction: 'outgoing' | 'incoming';
};
/** One path to walk from the start components. */
type ViewPreviewPathInput = {
    /** The ordered steps of this path, from the start components outwards. */
    steps: ViewPreviewPathStepInput[] & tags.MinItems<1>;
};
/** Which components and relationships to show: the start components and the paths to walk from them. */
type ViewPreviewTraversalInput = {
    /** Ids of the components the traversal starts from; at least one is required. Use real component ids returned by your other tools; never invent them. All start components should share one component type. */
    startSet: string[] & tags.MinItems<1>;
    /** The paths to walk from the start components, each an object with its ordered steps, e.g. `[{"steps": [stepA]}, {"steps": [stepB, stepC]}]`. Multi-step paths reach further, e.g. database -> applications -> business processes. Use one path per distinct kind of relationship to show. May be empty to show only the start components. */
    paths: ViewPreviewPathInput[];
    /** Set to `descendants` to expand the start components to all their descendants (children, grandchildren, and so on) before following the paths, e.g. the capabilities under Customer Management. The paths then start from every component in the expanded set. Leave unset to start from the start components only. */
    startSetExpansion?: 'descendants';
};
/** Numeric fields that place and size the bubbles in the `bubble` view. */
type ViewPreviewBubbleFieldsInput = {
    /** Numeric field for the x axis. */
    x?: string;
    /** Numeric field for the y axis. */
    y?: string;
    /** Numeric field for the bubble size. */
    size?: string;
};
/** How to draw the traversal's components and relationships. */
type ViewPreviewStyleInput = {
    /** Selects the view style; pick the one that fits the question. For how components connect, depend on each other or are grouped, prefer `modernizedBlockDiagram` or `dependencyMap`. `dependencyMap` (Dependency Map): the components each path step reaches, nested under the components they were reached from, so it reads as a hierarchical list; best for following dependency chains and impact from the start components, e.g. what depends on a database, what it depends on, or what is affected if it is removed. It stays readable on larger sets. `modernizedBlockDiagram` (Block Diagram): components drawn as nested blocks, children inside their parents, with the references between them as directional lines; best for what components connect to and how they flow, e.g. what an application exchanges data with or how a set of systems integrate, and for hierarchies and containment, e.g. what is "under", "inside" or "grouped by" something. `timeline` (Timeline): the start components as rows on a time axis, with date ranges drawn as phases and the fields in `milestoneFields` as milestones; best for anything that unfolds over time, e.g. roadmaps, lifecycles, or when things start, end or overlap. Needs date fields. `bubble` (Bubble chart): the start components plotted by numeric fields on the x and y axes, with an optional field for bubble size; best for comparing components on two measures at once and spotting clusters and outliers, e.g. business value against technical fit. Needs number fields. `relationships3` (Relationships): the components the paths reach drawn as a free-form network, with the references between them as lines; best when the network itself is the point, e.g. the user asks for a network, or where the clusters are in a densely interconnected set. */
    viewName: ViewPreviewStyle['viewName'];
    /** Date fields to show as milestone markers in the `timeline` view, by the field `name` from `get_metamodel_fields` (its label is also accepted), case-sensitive, e.g. `["live_end_date"]`. Name the specific field or fields relevant to the question; `"all"` shows every date field and can be noisy. */
    milestoneFields?: string[] | 'all';
    /** Numeric fields to plot in the `bubble` view, by the field `name` from `get_metamodel_fields` (its label is also accepted), case-sensitive, e.g. `{"x": "direct_cost", "y": "business_value"}`. `x` and `y` set the axes; `size` sets the bubble size and is optional, since bubbles get a default size without it. Only number fields can be plotted, and components with no value in a chosen field are left out. */
    bubbleFields?: ViewPreviewBubbleFieldsInput;
};
// The LLM-facing input sent as `renderSchemas.viewPreview`. The doc comments are the model's
// field descriptions. The backend unwraps each path's `steps` into the fence's
// `DirectedTriple[][]`, so the render payload is a `ViewPreviewPayload`.
/** A view preview: the traversal says which components and relationships to show, the view says how to draw them. */
export type ViewPreviewInput = {
    /** Which components and relationships to show: the start components and the paths to walk from them. */
    traversal: ViewPreviewTraversalInput;
    /** How to draw the components and relationships the traversal reaches. */
    view: ViewPreviewStyleInput;
};
export const VIEW_PREVIEW_RENDER_SCHEMA = {
    version: 1,
    schema: {
        version: "3.1",
        components: {
            schemas: {
                ViewPreviewInput: {
                    type: "object",
                    properties: {
                        traversal: {
                            $ref: "#/components/schemas/ViewPreviewTraversalInput",
                            description: "Which components and relationships to show: the start components and the paths to walk from them."
                        },
                        view: {
                            $ref: "#/components/schemas/ViewPreviewStyleInput",
                            description: "How to draw the components and relationships the traversal reaches."
                        }
                    },
                    required: [
                        "traversal",
                        "view"
                    ],
                    description: "A view preview: the traversal says which components and relationships to show, the view says how to draw them."
                },
                ViewPreviewTraversalInput: {
                    type: "object",
                    properties: {
                        startSet: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            minItems: 1,
                            description: "Ids of the components the traversal starts from; at least one is required. Use real component ids returned by your other tools; never invent them. All start components should share one component type."
                        },
                        paths: {
                            type: "array",
                            items: {
                                $ref: "#/components/schemas/ViewPreviewPathInput"
                            },
                            description: "The paths to walk from the start components, each an object with its ordered steps, e.g. `[{\"steps\": [stepA]}, {\"steps\": [stepB, stepC]}]`. Multi-step paths reach further, e.g. database -> applications -> business processes. Use one path per distinct kind of relationship to show. May be empty to show only the start components."
                        },
                        startSetExpansion: {
                            "const": "descendants",
                            description: "Set to `descendants` to expand the start components to all their descendants (children, grandchildren, and so on) before following the paths, e.g. the capabilities under Customer Management. The paths then start from every component in the expanded set. Leave unset to start from the start components only."
                        }
                    },
                    required: [
                        "startSet",
                        "paths"
                    ],
                    description: "Which components and relationships to show: the start components and the paths to walk from them."
                },
                ViewPreviewPathInput: {
                    type: "object",
                    properties: {
                        steps: {
                            type: "array",
                            items: {
                                $ref: "#/components/schemas/ViewPreviewPathStepInput"
                            },
                            minItems: 1,
                            description: "The ordered steps of this path, from the start components outwards."
                        }
                    },
                    required: [
                        "steps"
                    ],
                    description: "One path to walk from the start components."
                },
                ViewPreviewPathStepInput: {
                    type: "object",
                    properties: {
                        sourceType: {
                            type: "string",
                            description: "Component type name at the reference's source end, exactly as the metamodel spells it (case-sensitive). This is the reference's own source, whichever way the step is walked."
                        },
                        referenceType: {
                            type: "string",
                            description: "Reference type name, exactly as the metamodel spells it (case-sensitive), e.g. `Depends on`."
                        },
                        targetType: {
                            type: "string",
                            description: "Component type name at the reference's target end, exactly as the metamodel spells it (case-sensitive). This is the reference's own target, whichever way the step is walked."
                        },
                        direction: {
                            oneOf: [
                                {
                                    "const": "outgoing"
                                },
                                {
                                    "const": "incoming"
                                }
                            ],
                            description: "Which way to walk the reference. `outgoing` walks from source to target (what X points to, e.g. what X depends on); `incoming` walks from target to source (what points to X, e.g. what depends on X). The first step starts from the start components, so their type must be `sourceType` for `outgoing` and `targetType` for `incoming`; each later step starts from the type the previous step ended on."
                        }
                    },
                    required: [
                        "sourceType",
                        "referenceType",
                        "targetType",
                        "direction"
                    ],
                    description: "One step of a path: a reference type, its two component types, and which way to walk it."
                },
                ViewPreviewStyleInput: {
                    type: "object",
                    properties: {
                        viewName: {
                            oneOf: [
                                {
                                    "const": "bubble"
                                },
                                {
                                    "const": "timeline"
                                },
                                {
                                    "const": "dependencyMap"
                                },
                                {
                                    "const": "relationships3"
                                },
                                {
                                    "const": "modernizedBlockDiagram"
                                }
                            ],
                            description: "Selects the view style; pick the one that fits the question. For how components connect, depend on each other or are grouped, prefer `modernizedBlockDiagram` or `dependencyMap`. `dependencyMap` (Dependency Map): the components each path step reaches, nested under the components they were reached from, so it reads as a hierarchical list; best for following dependency chains and impact from the start components, e.g. what depends on a database, what it depends on, or what is affected if it is removed. It stays readable on larger sets. `modernizedBlockDiagram` (Block Diagram): components drawn as nested blocks, children inside their parents, with the references between them as directional lines; best for what components connect to and how they flow, e.g. what an application exchanges data with or how a set of systems integrate, and for hierarchies and containment, e.g. what is \"under\", \"inside\" or \"grouped by\" something. `timeline` (Timeline): the start components as rows on a time axis, with date ranges drawn as phases and the fields in `milestoneFields` as milestones; best for anything that unfolds over time, e.g. roadmaps, lifecycles, or when things start, end or overlap. Needs date fields. `bubble` (Bubble chart): the start components plotted by numeric fields on the x and y axes, with an optional field for bubble size; best for comparing components on two measures at once and spotting clusters and outliers, e.g. business value against technical fit. Needs number fields. `relationships3` (Relationships): the components the paths reach drawn as a free-form network, with the references between them as lines; best when the network itself is the point, e.g. the user asks for a network, or where the clusters are in a densely interconnected set."
                        },
                        milestoneFields: {
                            oneOf: [
                                {
                                    "const": "all"
                                },
                                {
                                    type: "array",
                                    items: {
                                        type: "string"
                                    }
                                }
                            ],
                            description: "Date fields to show as milestone markers in the `timeline` view, by the field `name` from `get_metamodel_fields` (its label is also accepted), case-sensitive, e.g. `[\"live_end_date\"]`. Name the specific field or fields relevant to the question; `\"all\"` shows every date field and can be noisy."
                        },
                        bubbleFields: {
                            $ref: "#/components/schemas/ViewPreviewBubbleFieldsInput",
                            description: "Numeric fields to plot in the `bubble` view, by the field `name` from `get_metamodel_fields` (its label is also accepted), case-sensitive, e.g. `{\"x\": \"direct_cost\", \"y\": \"business_value\"}`. `x` and `y` set the axes; `size` sets the bubble size and is optional, since bubbles get a default size without it. Only number fields can be plotted, and components with no value in a chosen field are left out."
                        }
                    },
                    required: [
                        "viewName"
                    ],
                    description: "How to draw the traversal's components and relationships."
                },
                ViewPreviewBubbleFieldsInput: {
                    type: "object",
                    properties: {
                        x: {
                            type: "string",
                            description: "Numeric field for the x axis."
                        },
                        y: {
                            type: "string",
                            description: "Numeric field for the y axis."
                        },
                        size: {
                            type: "string",
                            description: "Numeric field for the bubble size."
                        }
                    },
                    required: [],
                    description: "Numeric fields that place and size the bubbles in the `bubble` view."
                }
            }
        },
        schema: {
            $ref: "#/components/schemas/ViewPreviewInput"
        }
    } as import("typia").IJsonSchemaUnit<"3.1">,
} as const;
